import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getAccessToken } from './api';

/**
 * Subscribes to `/api/v1/drives/:id/stream`. Invalidates TanStack Query keys on:
 *   - `application.stage.changed` → refetch applications for this drive
 *   - `interview.rescheduled` → refetch interviews
 *   - `offer.uploaded` / `offer.released` → refetch offers
 *
 * Uses the same fetch+ReadableStream reader as `useNotificationStream` — EventSource can't
 * carry an Authorization header.
 */
export function useDriveStream(driveId: string | undefined) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!driveId) return;
    const token = getAccessToken();
    if (!token) return;
    const base = import.meta.env.VITE_API_BASE_URL || '/api/v1';
    const url = `${base}/drives/${driveId}/stream`;

    let closed = false;
    const controller = new AbortController();

    (async () => {
      try {
        const res = await fetch(url, {
          headers: { authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok || !res.body) return;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (!closed) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() ?? '';
          for (const raw of events) {
            const lines = raw.split('\n');
            let evName = 'message';
            for (const l of lines) if (l.startsWith('event:')) evName = l.slice(6).trim();
            if (evName === 'application.stage.changed') {
              qc.invalidateQueries({ queryKey: ['applications', driveId] });
              qc.invalidateQueries({ queryKey: ['drives', driveId] });
            } else if (evName === 'interview.rescheduled' || evName === 'interview.scheduled') {
              qc.invalidateQueries({ queryKey: ['interviews', driveId] });
            } else if (evName.startsWith('offer.')) {
              qc.invalidateQueries({ queryKey: ['offers'] });
            }
          }
        }
      } catch {
        // ignore — remounting the component reconnects
      }
    })();

    return () => {
      closed = true;
      controller.abort();
    };
  }, [driveId, qc]);
}
